import { Sparkles } from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Spinner } from '../Spinner'

interface EmptyStatePanelProps {
  heading: ReactNode
  hint: ReactNode
  suggestions: string[]
  onSuggest: (label: string) => void
  onGenerate?: (label: string) => void
  generateAriaLabel?: (label: string) => string
  isGenerating?: boolean
  className?: string
}

function Chip({
  label,
  onSuggest,
  onGenerate,
  generateAriaLabel,
  isGeneratingThis,
  isInactive,
}: {
  label: string
  onSuggest: () => void
  onGenerate?: () => void
  generateAriaLabel?: (label: string) => string
  isGeneratingThis: boolean
  isInactive: boolean
}) {
  const dividerClass = onGenerate
    ? 'border-r-2 border-ink dark:border-dark-border transition-[border-color] duration-150'
    : ''
  const inactiveClass = isInactive ? 'pointer-events-none' : ''

  return (
    <div className="group inline-flex items-stretch border-2 border-ink dark:border-dark-border rounded-lg overflow-hidden bg-surface-card dark:bg-dark-card shadow-[2px_2px_0_#111111] dark:shadow-[2px_2px_0_#000000] hover:-translate-x-px hover:-translate-y-px hover:shadow-[3px_3px_0_#111111] dark:hover:shadow-[3px_3px_0_#000000] active:translate-x-px active:translate-y-px active:shadow-none transition-all duration-150">
      <button
        type="button"
        onClick={onSuggest}
        disabled={isGeneratingThis}
        className={`px-3.5 py-2 text-[13px] font-extrabold bg-transparent text-ink dark:text-dark-ink hover:bg-surface-muted dark:hover:bg-dark-bg2 disabled:cursor-not-allowed transition-colors duration-100 ${dividerClass} ${inactiveClass}`}
      >
        {label}
      </button>
      {onGenerate && (
        <button
          type="button"
          onClick={onGenerate}
          disabled={isGeneratingThis}
          aria-label={
            generateAriaLabel
              ? generateAriaLabel(label)
              : `Generate pros and cons for ${label}`
          }
          className={`px-2.5 py-2 bg-transparent text-ink dark:text-dark-ink hover:bg-cyan disabled:cursor-not-allowed transition-colors duration-100 flex items-center ${inactiveClass}`}
        >
          {isGeneratingThis ? (
            <Spinner size="xs" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
        </button>
      )}
    </div>
  )
}

export function EmptyStatePanel({
  heading,
  hint,
  suggestions,
  onSuggest,
  onGenerate,
  generateAriaLabel,
  isGenerating = false,
  className = '',
}: EmptyStatePanelProps) {
  const [activeLabel, setActiveLabel] = useState<string | null>(null)

  useEffect(() => {
    if (!isGenerating) setActiveLabel(null)
  }, [isGenerating])

  function handleGenerate(label: string) {
    setActiveLabel(label)
    onGenerate?.(label)
  }

  return (
    <div
      className={`bg-surface-card dark:bg-dark-card border-[2.5px] border-ink dark:border-dark-border rounded-[14px] shadow-[5px_5px_0_#111111] dark:shadow-[5px_5px_0_#000000] overflow-hidden transition-colors ${className}`}
    >
      {/* Accent bar: left = pros green / right = cons red */}
      <div className="h-1.5 flex">
        <div className="flex-1" style={{ background: 'var(--zine-pro)' }} />
        <div className="flex-1" style={{ background: 'var(--zine-con)' }} />
      </div>

      <div className="flex flex-col items-center text-center pt-10 pb-11 px-8">
        <h2 className="font-black text-[28px] leading-[1.2] tracking-[-0.02em] text-ink dark:text-dark-ink">
          {heading}
        </h2>
        <p className="mt-2.5 text-sm font-semibold text-ink-secondary dark:text-dark-ink2">
          {hint}
        </p>
        <div className="flex flex-wrap justify-center gap-2.5 mt-7">
          {suggestions.map((label) => (
            <Chip
              key={label}
              label={label}
              onSuggest={() => onSuggest(label)}
              onGenerate={onGenerate ? () => handleGenerate(label) : undefined}
              generateAriaLabel={generateAriaLabel}
              isGeneratingThis={isGenerating && activeLabel === label}
              isInactive={isGenerating && activeLabel !== label}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
